import { Link, useParams, useSearchParams } from "react-router-dom"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useEffect, useState } from "react"
import { Skeleton } from "@/components/ui/skeleton"
import { toast } from "@/hooks/use-toast"
import {
  approveInscricao,
  getCalendarioForPessoaAction,
  reproveInscricao,
} from "../service/EventsService"
import { Calendar, MapPin, Users, Check, X, ChevronLeft } from "lucide-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Checkbox } from "@/components/ui/checkbox"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button" // Import Button for consistency
import { EventoDataType } from "@/types/events"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { isPast } from "date-fns" // Import isPast from date-fns
import { isInscricaoEncerrada } from "@/utils/date"

// --- COMPONENTE DE LINHA DA LISTA (HORIZONTAL) ---
const EventListRow = ({
  evento,
  onApprove,
  onReject,
}: {
  evento: EventoDataType
  onApprove: (e: EventoDataType) => void
  onReject: (e: EventoDataType) => void
}) => {
  const isPendente = evento.status_pes_desc === "pendente"
  const isEventPast = isPast(new Date(evento.data_inicio))
  const isEnrollmentClosed = isInscricaoEncerrada(evento.data_limite)

  return (
    <div className="flex w-full flex-col items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition-all hover:bg-white/10 md:flex-row">
      {/* Badge de Data */}
      <div className="flex min-w-[80px] flex-col items-center justify-center rounded-lg border border-white/5 bg-zinc-800 px-3 py-2">
        <span className="text-[10px] font-bold uppercase text-zinc-400">
          {new Date(evento.data_inicio).toLocaleDateString("pt-BR", { month: "short" })}
        </span>
        <span className="text-xl font-black text-white">
          {new Date(evento.data_inicio).getDate()}
        </span>
      </div>

      {/* Informações do Evento */}
      <div className="min-w-0 flex-1 space-y-1">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span className="rounded-full border border-blue-500/20 bg-blue-500/20 px-2 py-0.5 text-[10px] font-bold uppercase text-blue-400">
            {evento.formato}
          </span>
          <h3 className="truncate text-wrap font-semibold text-white">{evento.descricao}</h3>
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400">
          <span className="flex items-center gap-1">
            <MapPin size={12} /> {evento.endereco || "Local não informado"}
          </span>
          <span className="flex items-center gap-1">
            <Users size={12} /> {evento.inscritos} inscritos
          </span>
          <span className="flex items-center gap-1">
            <Calendar size={12} />
            {new Date(evento.data_inicio).toLocaleTimeString("pt-BR", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>
      </div>

      {/* Ações Condicionais */}
      <div className="ml-auto flex items-center gap-2">
        {isPendente && isEventPast ? (
          <div className="rounded-md border border-zinc-500/20 bg-zinc-500/5 px-3 py-1 text-[10px] font-bold uppercase text-zinc-500">
            Evento encerrado
          </div>
        ) : isPendente && isEnrollmentClosed ? (
          <div className="rounded-md border border-zinc-500/20 bg-zinc-500/5 px-3 py-1 text-[10px] font-bold uppercase text-zinc-500">
            Inscrições encerradas
          </div>
        ) : isPendente ? (
          <>
            <Button
              variant="destructive"
              onClick={() => onReject(evento)}
              className="flex items-center gap-1.5 rounded-lg bg-red-500/10 px-3 py-2 text-xs font-bold text-red-500 transition-colors hover:bg-red-500/20"
            >
              <X size={14} /> Recusar
            </Button>
            <Button
              variant="default"
              onClick={() => onApprove(evento)}
              className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white shadow-lg shadow-emerald-900/20 transition-colors hover:bg-emerald-500"
            >
              <Check size={14} /> Aprovar
            </Button>
          </>
        ) : (
          <div
            className={`rounded-md border px-3 py-1 text-[10px] font-bold uppercase ${
              evento.status_pes_desc === "autorizado"
                ? "border-emerald-400/20 bg-emerald-400/5 text-emerald-400"
                : "border-zinc-500/20 bg-zinc-500/5 text-zinc-500"
            }`}
          >
            {evento.status_pes_desc}
          </div>
        )}
      </div>
    </div>
  )
}

// --- LISTAGEM E LÓGICA DE DECISÃO ---
const MemberSubscriptionEventsList = ({
  memberId,
  statusFilter,
}: {
  memberId: string | number
  statusFilter: string
}) => {
  const [events, setEvents] = useState<EventoDataType[]>([])
  const [loading, setLoading] = useState(true)
  const { user } = useAuth()

  // Estados para Modais e Ações
  const [selectedEvent, setSelectedEvent] = useState<EventoDataType | null>(null)
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState("")
  const [dontShowAgain, setDontShowAgain] = useState(false)

  const loadEvents = async () => {
    try {
      setLoading(true)
      const response = await getCalendarioForPessoaAction({ pes_id: Number(memberId) })
      let data = response.data.data
      if (statusFilter !== "all") {
        data = data.filter((e) => e.status_pes_desc === statusFilter)
      }
      setEvents(data)
    } catch (err) {
      toast({
        title: "Erro",
        description: err.data.message,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadEvents()
  }, [memberId, statusFilter])

  // Lógica de Aprovação com Checkbox de Memória
  const handleApprove = (evento: EventoDataType) => {
    setSelectedEvent(evento)
    const skip = localStorage.getItem("skip_approve_confirm") === "true"
    if (skip) {
      confirmApprove(evento)
    } else {
      setShowApproveModal(true)
    }
  }

  // Lógica de Reprovacao
  const handleReject = (evento: EventoDataType) => {
    setSelectedEvent(evento)
    setShowRejectModal(true)
  }

  const confirmApprove = async (event: EventoDataType) => {
    if (dontShowAgain) localStorage.setItem("skip_approve_confirm", "true")

    const payload = {
      pes_id: [memberId],
      originador_id: user.pesId,
      id_inscricao: event.id_inscricao,
      origem: event.origem,
      status: "aprovar",
    }

    await approveInscricao(payload)
      .then(() => {
        toast({ title: "Sucesso", description: "Inscrição aprovada!" })
        loadEvents()
      })
      .catch((err) => {
        toast({ title: "Erro", description: err.response.data.message })
      })
      .finally(() => {
        setShowApproveModal(false)
      })
  }

  const confirmReject = async () => {
    if (!rejectReason)
      return toast({ title: "Atenção", description: "Insira um motivo.", variant: "destructive" })

    const payload = {
      pes_id: [memberId],
      originador_id: user.pesId,
      origem: selectedEvent.origem,
      id_inscricao: selectedEvent.id_inscricao,
      status: "reprovar",
      motivo: rejectReason,
    }

    await reproveInscricao(payload)
      .then(() => {
        toast({ title: "Sucesso", description: "Inscrição recusada!" })
        setRejectReason("")
        loadEvents()
      })
      .catch((err) => {
        toast({ title: "Erro", description: err.response.data.message })
      })
      .finally(() => {
        setShowRejectModal(false)
      })
  }

  if (loading)
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            className="h-20 w-full rounded-xl"
          />
        ))}
      </div>
    )

  return (
    <div className="mt-4 flex flex-col gap-3">
      {events.length > 0 ? (
        events.map((ev) => (
          <EventListRow
            key={ev.id_inscricao}
            evento={ev}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        ))
      ) : (
        <div className="rounded-2xl border-2 border-dashed border-white/5 py-12 text-center text-zinc-500">
          Nenhuma inscrição encontrada.
        </div>
      )}

      {/* Modal: Aprovação */}
      <AlertDialog
        open={showApproveModal}
        onOpenChange={setShowApproveModal}
      >
        <AlertDialogContent className="border-zinc-800 bg-zinc-950 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Aprovação</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Deseja autorizar a participação no evento: <br />
              <strong className="text-white">{selectedEvent?.descricao}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-4 flex items-center gap-2 border-t border-white/5 py-4">
            <Checkbox
              id="memo"
              checked={dontShowAgain}
              onCheckedChange={(v) => setDontShowAgain(!!v)}
            />
            <label
              htmlFor="memo"
              className="cursor-pointer text-sm text-zinc-400"
            >
              Não perguntar novamente
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-800 bg-transparent hover:bg-white/5">
              Voltar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmApprove(selectedEvent)}
              className="bg-emerald-600 hover:bg-emerald-500"
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal: Recusa com Motivo */}
      <AlertDialog
        open={showRejectModal}
        onOpenChange={setShowRejectModal}
      >
        <AlertDialogContent className="border-zinc-800 bg-zinc-950 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-500">Motivo da Recusa</AlertDialogTitle>
          </AlertDialogHeader>
          <Textarea
            placeholder="Ex: O membro não cumpre os pré-requisitos da turma..."
            className="min-h-[100px] border-zinc-800 bg-zinc-900"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-800 bg-transparent hover:bg-white/5">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmReject}
              className="bg-red-600 text-white hover:bg-red-500"
            >
              Confirmar Recusa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// --- PÁGINA PRINCIPAL ---
export const EventsMemberSubscriptionsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { id: memberId, name } = useParams()
  const activeTab = searchParams.get("activeTab") || "all"

  const handleTabChange = (value: string) => {
    setSearchParams((prev) => {
      prev.set("activeTab", value)
      return prev
    })
  }

  const getStatusFilter = (tab: string) => {
    const map: Record<string, string> = {
      pendentes: "pendente",
      autorizados: "autorizado",
      recusados: "recusado",
    }
    return map[tab] || "all"
  }

  return (
    <div className="p-6">
      <header className="flex flex-col gap-2">
        <Link
          to="/subscriptions?activeTab=by-member"
          className="group flex items-center gap-2 text-sm text-zinc-500 transition-colors hover:text-white"
        >
          <ChevronLeft
            size={16}
            className="transition-transform group-hover:-translate-x-1"
          />{" "}
          Voltar para lista
        </Link>
        <h1 className="py-4 text-2xl text-white">
          Inscrições de <strong className="text-orange-600">{name}</strong>
        </h1>
      </header>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <TabsList className="border border-white/10 bg-white/5 p-1">
          <TabsTrigger value="all">Todos</TabsTrigger>
          <TabsTrigger value="pendentes">Pendentes</TabsTrigger>
          <TabsTrigger value="autorizados">Autorizados</TabsTrigger>
          <TabsTrigger value="recusados">Recusados</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <MemberSubscriptionEventsList
            memberId={memberId}
            statusFilter={getStatusFilter(activeTab)}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
